import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Dayjs } from 'dayjs';
import isEqual from 'lodash/isEqual';
import { useSnackbar } from 'notistack';

import { Alert, Box, Button, Stack } from '@mui/material';

import { ROLE_ADMIN_USER, ROLE_HOTEL_COORDINATOR } from '~/consts/roles';

import { offerQueryKeys } from '~/hooks/apis/offers/useGetOffer';

import OffersService from '../../../services/OffersService';
import { defaultEndDate, defaultStartDate } from '../../../utils/DefaultDate';
import { reportError } from '../../../utils/reportError';
import { scheduleForBrandAndType } from '../../../utils/scheduleForBrandAndType';
import PermissionedComponent from '../PermissionedComponent';

import NewScheduleFormBase from './NewScheduleFormBase';

interface Props {
  idList: Array<number>;
  offerId: string;
  brands: Array<string>;
  disabled?: boolean;
  onSave?: () => void;
  onSuccess?: (schedule: { start: string; end: string }) => void;
  type: string;
  regions: Array<{ code: string }>;
  end?: string;
  start?: string;
  enableUtilBtn?: boolean;
  isDeleteAllowed?: boolean;
  onApplyToAll?: (data: { schedule: { start: string; end: string } }) => void;
  schedules?: Array<App.Schedule>;
  brand?: string;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  fullWidth?: boolean;
}

export default function NewScheduleForm({
  idList: idListProp,
  offerId,
  brands,
  disabled = false,
  onSave,
  onSuccess,
  type,
  regions,
  end: propEnd,
  start: propStart,
  enableUtilBtn,
  isDeleteAllowed,
  onApplyToAll: onApplyToAllProp,
  schedules = [],
  brand = '',
  minDate,
  maxDate,
  fullWidth,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [startUpdated, setStartUpdated] = useState(false);
  const [endUpdated, setEndUpdated] = useState(false);
  const [schedule, setSchedule] = useState({
    start: propStart || defaultStartDate(),
    end: propEnd || defaultEndDate(),
  });
  const [idList, setIdList] = useState(idListProp);
  const [errors, setErrors] = useState<Array<{ message: string }>>([]);

  const prevPropStartRef = useRef(propStart);
  const prevPropEndRef = useRef(propEnd);
  const prevIdListRef = useRef(idListProp);

  // Sync start when parent prop changes (not on initial mount)
  useEffect(() => {
    if (propStart !== prevPropStartRef.current) {
      prevPropStartRef.current = propStart;
      setSchedule((prev) => ({ ...prev, start: propStart || defaultStartDate() }));
      setStartUpdated(true);
    }
  }, [propStart]);

  // Sync end when parent prop changes (not on initial mount)
  useEffect(() => {
    if (propEnd !== prevPropEndRef.current) {
      prevPropEndRef.current = propEnd;
      setSchedule((prev) => ({ ...prev, end: propEnd || defaultEndDate() }));
      setEndUpdated(true);
    }
  }, [propEnd]);

  // Sync idList when parent prop changes
  useEffect(() => {
    const sortedProp = [...idListProp].sort();
    const sortedPrev = [...prevIdListRef.current].sort();
    if (!isEqual(sortedProp, sortedPrev)) {
      prevIdListRef.current = idListProp;
      setIdList(idListProp);
    }
  }, [idListProp]);

  const updateStart = useCallback((time: string) => {
    setSchedule((prev) => ({ ...prev, start: time }));
    setStartUpdated(true);
  }, []);

  const updateEnd = useCallback((time: string) => {
    setSchedule((prev) => ({ ...prev, end: time }));
    setEndUpdated(true);
  }, []);

  const onError = useCallback((data: { errors?: Array<{ message: string }> }) => {
    setErrors(data.errors || []);
  }, []);

  const onSubmit = useCallback(() => {
    if (isSaving) {
      return;
    }

    setErrors([]);
    setIsSaving(true);
    if (onSave) {
      onSave();
    }

    const regionCodes = regions.map((r) => r.code);
    const payload = {
      type,
      regions: regionCodes,
      start: new Date(schedule.start).toISOString(),
      end: new Date(schedule.end).toISOString(),
      brands,
    };

    if (idList.length > 0) {
      OffersService.updateScheduleList({ idList, ...payload }, offerId)
        .then((result) => {
          setIdList(result.map((s) => s.id));
          setIsSaving(false);
          setStartUpdated(false);
          setEndUpdated(false);
          queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) });
          enqueueSnackbar('Schedule saved', { variant: 'success' });
          if (onSuccess) {
            onSuccess(schedule);
          }
        })
        .catch((e) => {
          setIsSaving(false);
          onError(e);
          reportError(e);
        });
    } else {
      OffersService.createScheduleList(payload, offerId)
        .then((newSchedule) => {
          const updatedSchedule = { start: newSchedule[0].start, end: newSchedule[0].end };
          setSchedule(updatedSchedule);
          setIdList(newSchedule.map((s) => s.id));
          setIsSaving(false);
          setStartUpdated(false);
          setEndUpdated(false);
          queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) });
          enqueueSnackbar('Schedule created', { variant: 'success' });
          if (onSuccess) {
            onSuccess(updatedSchedule);
          }
        })
        .catch((e) => {
          setIsSaving(false);
          onError(e);
          reportError(e);
        });
    }
  }, [
    isSaving,
    idList,
    offerId,
    brands,
    regions,
    type,
    schedule,
    onSave,
    onSuccess,
    onError,
    enqueueSnackbar,
    queryClient,
  ]);

  const onDelete = useCallback(() => {
    OffersService.deleteScheduleList(offerId, { idList })
      .then(() => {
        setIdList([]);
        queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) });
      })
      .catch((e) => {
        onError(e);
        reportError(e);
      });
  }, [offerId, idList, queryClient, onError]);

  const onReset = useCallback(() => {
    setSchedule({
      start: scheduleForBrandAndType({ schedules, brand, type, scheduleKey: 'start' }) || defaultStartDate(),
      end: scheduleForBrandAndType({ schedules, brand, type, scheduleKey: 'end' }) || defaultEndDate(),
    });
  }, [schedules, brand, type]);

  const onApplyToAll = useCallback(() => {
    if (onApplyToAllProp) {
      onApplyToAllProp({ schedule: { start: schedule.start, end: schedule.end } });
    }
  }, [onApplyToAllProp, schedule]);

  // For existing schedules (idList.length > 0), only enable Save when dates were changed.
  // For new schedules (Create), always enable since there's nothing to compare against.
  const isModified = idList.length === 0 || startUpdated || endUpdated;
  const buttonLabel = isSaving ? 'Saving...' : idList.length > 0 ? 'Save' : 'Create';

  return (
    <Box>
      {errors.length > 0 && (
        <Box mb={2}>
          {errors.map((error, index) => (
            <Alert key={index} severity="error" sx={{ mb: 1 }}>
              {error.message}
            </Alert>
          ))}
        </Box>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between">
        <NewScheduleFormBase
          disabled={disabled || isSaving}
          schedule={schedule}
          updateStart={updateStart}
          updateEnd={updateEnd}
          startUpdated={startUpdated}
          endUpdated={endUpdated}
          onReset={onReset}
          onApplyToAll={onApplyToAll}
          enableUtilBtn={enableUtilBtn}
          minDate={minDate}
          maxDate={maxDate}
          fullWidth={fullWidth}
        />
        <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_HOTEL_COORDINATOR]}>
          <Stack direction="row" spacing={1} mt={{ xs: 1, sm: 0 }}>
            <Button variant="contained" disabled={disabled || isSaving || !isModified} onClick={onSubmit}>
              {buttonLabel}
            </Button>
            {isDeleteAllowed && idList.length > 0 && (
              <Button variant="contained" color="warning" onClick={onDelete}>
                Delete
              </Button>
            )}
          </Stack>
        </PermissionedComponent>
      </Stack>
    </Box>
  );
}
